import { gql } from '@apollo/client'
import {
  CORE_USER_PROFILE_FIELDS,
  USER_PROFILE_SOCIAL_FIELDS,
  USER_PROFILE_SUMMARY_FIELDS,
} from './fragments'

export const GET_CURRENT_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileCurrent($language: String, $contentLanguage: [String]) {
    userProfileCurrent(language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const GET_PROFILE_BY_UID = gql`
  ${CORE_USER_PROFILE_FIELDS}
  ${USER_PROFILE_SOCIAL_FIELDS}
  query UserProfileByUid($uid: ID!, $language: String, $contentLanguage: [String]) {
    userProfileByUid(uid: $uid, language: $language) {
      ...CoreUserProfileFields
      ...UserProfileSocialFields
    }
  }
`

export const UPDATE_PROFILE = gql`
  ${CORE_USER_PROFILE_FIELDS}
  mutation UserProfileUpdate($input: UserProfileUpdateInput!) {
    userProfileUpdate(input: $input) {
      profile {
        ...CoreUserProfileFields
      }
    }
  }
`

export const CREATE_FOLLOW = gql`
  mutation UserProfileFollow($uid: ID!) {
    userProfileFollow(uid: $uid) {
      profile {
        uid
        followerCount
        followingCount
        viewerContext {
          isFollowing
        }
      }
    }
  }
`

export const DELETE_FOLLOW = gql`
  mutation UserProfileUnfollow($uid: ID!) {
    userProfileUnfollow(uid: $uid) {
      profile {
        uid
        followerCount
        followingCount
        viewerContext {
          isFollowing
        }
      }
    }
  }
`

export const GET_FOLLOWERS = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowers($uid: ID!, $first: Int = 20, $after: String) {
    userProfileFollowers(uid: $uid, first: $first, after: $after) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_FOLLOWING = gql`
  ${USER_PROFILE_SUMMARY_FIELDS}
  query UserProfileFollowing($uid: ID!, $first: Int = 20, $after: String) {
    userProfileFollowing(uid: $uid, first: $first, after: $after) {
      edges {
        node {
          ...UserProfileSummaryFields
        }
        cursor
        followedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const GET_USER_TAG_OPTIONS = gql`
  query UserTagOptionsList($language: String) {
    userTagOptionsList(language: $language) {
      id
      tag
      category
      label
    }
  }
`

export const GET_MY_PLAYLISTS = gql`
  query MyPlaylists {
    playlistList(userCurrent: true, includePrivate: true) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        description
      }
    }
  }
`

export const GET_MEMBER_PLAYLISTS = gql`
  query MemberPlaylists($uid: ID!) {
    playlistList(
      where: { uid: { eq: $uid } }
      visibility: [PUBLIC, ANONYMOUS]
    ) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        itemCount
        description
      }
    }
  }
`

export const GET_PROFILE_PLAYLIST_ITEMS = gql`
  query ProfilePlaylistItems(
    $playlistId: ID!
    $playlistType: String!
    $language: [String]!
    $limit: Int!
  ) {
    playlistItemList(
      userCurrent: true
      playlists: [{ id: $playlistId, playlistType: $playlistType }]
      limit: $limit
      offset: 0
    ) {
      playlistItems {
        id
        contentId
        contentType
        playlistPosition
        content(language: $language) {
          id
          title
          path(format: RELATIVE)
          contentType
          contentId
          shortDescription
          featureMedia {
            duration
          }
          contentAssetReferences(
            contentAssetReferences: [{ type: "keyart-no-text" }]
            limit: 1
          ) {
            asset {
              renderUrl
            }
          }
          imageContentRelationships(
            types: ["d6-keyart-no-text", "d6-poster-image"]
            language: ["en", "_primary"]
          ) {
            type
            image {
              url
            }
          }
        }
      }
    }
  }
`

export const GET_PUBLIC_PLAYLIST_ITEMS = gql`
  query PublicPlaylistItems(
    $playlistId: ID!
    $playlistType: String!
    $language: [String]!
    $limit: Int!
  ) {
    playlistItemList(
      playlists: [{ id: $playlistId, playlistType: $playlistType }]
      limit: $limit
      offset: 0
    ) {
      playlistItems {
        id
        contentId
        contentType
        playlistPosition
        content(language: $language) {
          id
          title
          path(format: RELATIVE)
          contentType
          contentId
          shortDescription
          featureMedia {
            duration
          }
          contentAssetReferences(
            contentAssetReferences: [{ type: "keyart-no-text" }]
            limit: 1
          ) {
            asset {
              renderUrl
            }
          }
          imageContentRelationships(
            types: ["d6-keyart-no-text", "d6-poster-image"]
            language: ["en", "_primary"]
          ) {
            type
            image {
              url
            }
          }
        }
      }
    }
  }
`

export const GET_USER_META_TAGS = gql`
  query ProfileMetaTags {
    userMetaTagList(userCurrent: true) {
      userMetaTags {
        id
        name
        tagType
        viewCount
        viewSeconds
        weight
      }
    }
  }
`

// userHistoryList currently requires elevated roles (video-analytics service).
// The call is still made so the network request is visible for debugging.
export const GET_USER_HISTORY = gql`
  query ProfileUserHistory($limit: Int!) {
    userHistoryList(
      limit: $limit
      order: [["lastPlayed", "DESC"]]
    ) {
      userHistory {
        id
        mediaId
        position
        watched
        lastPlayed
        media {
          mediaId
          duration
          contentFeatured {
            title
            path
            contentId
            imageContentRelationships(types: ["poster"]) {
              type
              image {
                url
              }
            }
          }
        }
        userMediaPosition {
          position
          hiddenFromContinue
        }
      }
      count
    }
  }
`

// myCommunitySpaces does not exist on the stage GraphQL server.
// communitySpaceList exists but has no user filter, so "my spaces" cannot be queried.
// Uncomment when the backend deploys myCommunitySpaces.
// export const GET_COMMUNITY_SPACES = gql`
//   query ProfileCommunitySpaces($limit: Int) {
//     myCommunitySpaces(limit: $limit) {
//       edges {
//         node {
//           id
//           name
//           description
//           memberCount
//         }
//       }
//       pageInfo {
//         hasNextPage
//         endCursor
//       }
//     }
//   }
// `

// Profile picture upload: presigned S3 URL flow
// 1. Request presigned URL  2. PUT image to S3  3. Confirm upload
export const CREATE_PROFILE_PICTURE_UPLOAD = gql`
  mutation RequestProfilePictureUpload($fileExtension: String!) {
    requestProfilePictureUpload(fileExtension: $fileExtension) {
      uploadUrl
      imageUuid
      expiresAt
    }
  }
`

export const CREATE_PROFILE_PICTURE_CONFIRM = gql`
  mutation ConfirmProfilePictureUpload($imageUuid: ID!, $imageExtension: String!) {
    confirmProfilePictureUpload(imageUuid: $imageUuid, imageExtension: $imageExtension) {
      success
      profilePicture {
        url
        small
        card
      }
    }
  }
`

export const DELETE_PROFILE_PICTURE = gql`
  mutation DeleteProfilePicture {
    deleteProfilePicture {
      success
    }
  }
`

export const UPDATE_PLAYLIST_VISIBILITY = gql`
  mutation UpdatePlaylistVisibility($playlistId: ID!, $visibility: PlaylistVisibility!) {
    updatePlaylistVisibility(playlistId: $playlistId, visibility: $visibility) {
      playlist {
        id
        visibility
      }
    }
  }
`

export const GET_AVATAR_BY_UID = gql`
  query UserAvatarByUid($uid: ID!) {
    userProfileByUid(uid: $uid) {
      uid
      userInfo {
        uid
        avatar {
          url
          small
          card
        }
      }
    }
  }
`

export const GET_COMMENT_COUNT = gql`
  query ProfileCommentCount {
    commentList(userCurrent: true, limit: 1) {
      count
    }
  }
`
